import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { FileText, Images, Download, Eye } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { fileSrc, fmtSize, openFile } from "@/lib/resources";
import { ResourcesGate } from "@/components/resources/ResourcesGate";

const TABS = [{ id: "reports", label: "GAMLS Reports", icon: FileText }, { id: "gallery", label: "Gallery", icon: Images }];

const ReportCard = ({ r }) => (
  <div className="bg-white border border-[#E5E7EB] rounded-lg p-5 flex gap-4 hover:border-[#E60000] transition-colors" data-testid={`resource-report-${r.id}`}>
    <div className="w-12 h-14 shrink-0 rounded bg-[#FFF0F0] text-[#E60000] grid place-items-center font-bold text-[10px]">PDF</div>
    <div className="min-w-0 flex-1">
      <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-gray-500">{r.year || "Report"} · {fmtSize(r.size)}</div>
      <h3 className="font-heading font-bold text-[#111] mt-1">{r.title}</h3>
      {r.note && <p className="text-sm text-gray-600 mt-1">{r.note}</p>}
      <div className="flex gap-4 mt-3 text-sm font-semibold">
        <a href={fileSrc(r.url)} target="_blank" rel="noreferrer" onClick={(e) => { e.preventDefault(); openFile(r.url); }} className="inline-flex items-center gap-1.5 text-[#E60000] hover:underline" data-testid={`resource-report-view-${r.id}`}><Eye className="w-4 h-4" />View</a>
        <a href={`${fileSrc(r.url)}?download=1`} onClick={(e) => { e.preventDefault(); openFile(r.url, true); }} className="inline-flex items-center gap-1.5 hover:text-[#E60000]" data-testid={`resource-report-download-${r.id}`}><Download className="w-4 h-4" />Download</a>
      </div>
    </div>
  </div>
);

const AlbumCard = ({ a }) => (
  <Link to={`/resources/gallery/${a.id}`} className="group bg-white border border-[#E5E7EB] rounded-lg overflow-hidden hover:border-[#E60000] transition-colors" data-testid={`resource-album-${a.id}`}>
    <div className="aspect-[4/3] bg-[#F3F4F6] overflow-hidden">
      {a.cover_url ? <img src={fileSrc(a.cover_url)} alt={a.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /> : <div className="w-full h-full grid place-items-center text-gray-400"><Images className="w-8 h-8" /></div>}
    </div>
    <div className="p-4">
      <h3 className="font-heading font-bold text-[#111]">{a.title}</h3>
      <div className="text-xs text-gray-500 mt-1">{a.event_title ? `${a.event_title} · ` : ""}{a.photo_count} photo{a.photo_count === 1 ? "" : "s"}</div>
    </div>
  </Link>
);

const Empty = ({ text, testId }) => <div className="bg-white border border-dashed border-[#D1D5DB] rounded-lg p-10 text-center text-sm text-gray-500" data-testid={testId}>{text}</div>;

const ResourcesBody = () => {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === "gallery" ? "gallery" : "reports";
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => { api.get("/resources").then((r) => setData(r.data)).catch((e) => setError(errMsg(e))); }, []);
  return <div data-testid="resources-page">
    <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#E60000]">Congress documents</div>
    <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight mt-1" data-testid="resources-title">Resources</h1>
    <p className="text-sm sm:text-base text-gray-600 mt-2 max-w-2xl">Official GAMLS reports and photo albums from our events.</p>
    <div className="flex gap-2 mt-8 border-b border-[#E5E7EB]">
      {TABS.map((t) => <button key={t.id} onClick={() => setParams(t.id === "reports" ? {} : { tab: t.id })} className={`h-11 px-4 -mb-px inline-flex items-center gap-2 text-sm font-semibold border-b-2 transition-colors ${tab === t.id ? "border-[#E60000] text-[#111]" : "border-transparent text-gray-500 hover:text-[#111]"}`} data-testid={`resources-tab-${t.id}`}><t.icon className="w-4 h-4" />{t.label}</button>)}
    </div>
    <div className="mt-6">
      {error ? <p role="alert" className="text-sm text-[#B30000]" data-testid="resources-error">{error}</p> : !data ? <p className="text-gray-500">Loading…</p>
        : tab === "reports" ? (data.reports.length ? <div className="grid md:grid-cols-2 gap-4" data-testid="resources-reports">{data.reports.map((r) => <ReportCard key={r.id} r={r} />)}</div> : <Empty text="No reports have been published yet." testId="resources-reports-empty" />)
          : (data.albums.length ? <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="resources-albums">{data.albums.map((a) => <AlbumCard key={a.id} a={a} />)}</div> : <Empty text="No photo albums yet." testId="resources-albums-empty" />)}
    </div>
  </div>;
};

export default function Resources() {
  return <ResourcesGate><ResourcesBody /></ResourcesGate>;
}
